import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {reduxForm, Field, formValueSelector, change} from 'redux-form';

import classNames from 'classnames';

import Label from 'components/Label';
import FormField from 'components/FormField';
import ErrorBlock from 'components/ErrorBlock';
import MultipleFileUpload from 'components/MultipleFileUpload';
import SingleFileUpload from 'components/SingleFileUpload';
import {buildRoomSelectizeOption} from 'components/Fields/SearchRoomField';
import {
  SearchRoomField,
  CheckBoxField,
  TextAreaField,
  SelectField
} from 'components/Fields';

import {selectModal} from 'ducks/modals';

import {
  uploadFile,
  ACCEPTED_IMAGE_FILE_TYPES,
  ACCEPTED_DOCUMENT_FILE_TYPES
} from 'ducks/files';

import {
  MODALS,
  fetchRoomOptionsForAddArrivalRoomCheck,
  createArrivalRoomCheck
} from 'ducks/bookingSets';

import {headOption} from 'utils/common';
import {
  signRoomCheckImage as signRoomCheckImageSdk,
  signInventoryDocument as signInventoryDocumentSdk
} from 'sdk/bookingSets';

import imageIcon from 'assets/icons/image-icon.svg';

import inventoryIcon from 'assets/icons/inventory-icon.svg';

import './styles.css';

const formSubmit = (values, dispatch, props) => {
  const {
    room,
    existingDamages,
    description,
    imageIdentifiers,
    inventoryIdentifier
  } = values;
  const {bookingSetId} = props;

  const extraActions = props.getExtraActions();

  const data = {
    bookingSetId,
    booking: _.get(room, 'object.bookingId'),
    existingDamages,
    description,
    imageIdentifiers,
    inventoryIdentifier
  };

  dispatch(createArrivalRoomCheck(data, extraActions));
};

class AddArrivalRoomCheckForm extends React.Component<any> {
  state = {
    imageWrongFormat: false,
    images: [],
    inventoryWrongFormat: false,
    inventory: null
  };

  componentDidMount() {
    const {bookingSetId} = this.props;

    const extraActions = this.props.storeDataExtraActions(data => ({
      roomOptions: data
    }));

    this.props.fetchRoomOptionsForAddArrivalRoomCheck(
      {bookingSetId},
      extraActions
    );
  }

  onRoomChange = selectedRoom => {
    if (_.isNil(selectedRoom)) {
      return this.props.change('roomType', null);
    }

    const roomTypeOption = {
      value: _.get(selectedRoom, 'object.roomType'),
      label: _.get(selectedRoom, 'object.roomType')
    };

    this.props.change('roomType', roomTypeOption);
  };

  onImageDrop = images => {
    if (_.isEmpty(images)) {
      return this.setState({imageWrongFormat: true});
    }

    let uploadedImage = headOption(images);
    this.setState({
      imageWrongFormat: false
    });

    // Store identifiers in form in order to be verified when the form is submitted
    const imageIdentifiers = this.props.imageIdentifiers;
    const storeIdentifierAction = identifier => {
      // Attach identifier in order to remove the identifier of the chosen image when being removed
      uploadedImage.identifier = identifier;
      const images = [...this.state.images, uploadedImage];
      this.setState({images});

      return change(
        'addArrivalRoomCheck',
        'imageIdentifiers',
        _.xor(imageIdentifiers, [identifier])
      );
    };

    const {bookingSetId} = this.props;
    const signData = {
      filename: uploadedImage.name,
      fileType: uploadedImage.type,
      bookingSetId
    };

    this.props.uploadFile({
      file: uploadedImage,
      signSdk: signRoomCheckImageSdk,
      signData,
      storeIdentifierAction
    });
  };

  onImageRemove = image => {
    this.setState({images: _.xor(this.state.images, [image])}, () => {
      this.props.change(
        'imageIdentifiers',
        _.xor(this.props.imageIdentifiers, [image.identifier])
      );
    });
  };

  onInventoryDrop = inventories => {
    if (_.isEmpty(inventories)) {
      return this.setState({inventoryWrongFormat: true});
    }

    const uploadedInventory = headOption(inventories);
    this.setState({
      inventory: uploadedInventory,
      inventoryWrongFormat: false
    });

    // Store identifiers in form in order to be verified when the form is submitted
    const storeIdentifierAction = identifier =>
      change('addArrivalRoomCheck', 'inventoryIdentifier', identifier);

    const {bookingSetId} = this.props;
    const signData = {
      filename: uploadedInventory.name,
      fileType: uploadedInventory.type,
      bookingSetId
    };

    this.props.uploadFile({
      file: uploadedInventory,
      signSdk: signInventoryDocumentSdk,
      signData,
      storeIdentifierAction
    });
  };

  onInventoryRemove = () => {
    this.setState({inventory: null}, () =>
      this.props.change('inventoryIdentifier', null)
    );
  };

  render() {
    const {className, handleSubmit, error, roomOptions} = this.props;

    return (
      <form
        onSubmit={handleSubmit}
        className={classNames(className, 'AddArrivalRoomCheckForm')}>
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <FormField>
          <Label>Room No</Label>
          <SearchRoomField rooms={roomOptions} onChange={this.onRoomChange} />
        </FormField>
        <FormField>
          <Field name="roomType" component={SelectField} disabled={true} />
        </FormField>
        <FormField>
          <Field
            name="existingDamages"
            component={CheckBoxField}
            label="Existing damages?"
          />
        </FormField>
        <FormField className="description">
          <Label>Description</Label>
          <Field
            name="description"
            component={TextAreaField}
            placeholder="Some notes here..."
          />
        </FormField>
        <FormField>
          <MultipleFileUpload
            files={this.state.images}
            onFileDrop={this.onImageDrop}
            wrongFormat={this.state.imageWrongFormat}
            onFileRemove={this.onImageRemove}
            placeholderIcon={imageIcon}
            placeholderText="Upload images"
            acceptedFileTypes={ACCEPTED_IMAGE_FILE_TYPES}
            filesLabel="Images"
          />
        </FormField>
        <FormField>
          <SingleFileUpload
            file={this.state.inventory}
            onFileDrop={this.onInventoryDrop}
            wrongFormat={this.state.inventoryWrongFormat}
            onFileRemove={this.onInventoryRemove}
            acceptedFileTypes={ACCEPTED_DOCUMENT_FILE_TYPES}
            placeholderIcon={inventoryIcon}
            placeholderText="Upload inventory"
            fileLabel="Inventory"
          />
        </FormField>
      </form>
    );
  }
}

const formSelector = formValueSelector('addArrivalRoomCheck');

const mapStateToProps = state => {
  const modal = selectModal(state, MODALS.ADD_ARRIVAL_ROOM_CHECK);

  const roomOptions = _.get(modal, 'data.roomOptions', []);

  const initialRoom = headOption(roomOptions, buildRoomSelectizeOption);
  const initialRoomType = {
    value: _.get(initialRoom, 'object.roomType'),
    label: _.get(initialRoom, 'object.roomType')
  };

  const imageIdentifiers = formSelector(state, 'imageIdentifiers');

  return {
    initialValues: {
      room: initialRoom,
      roomType: initialRoomType,
      existingDamages: false,
      imageIdentifiers: [],
      inventoryIdentifier: null
    },
    roomOptions,
    imageIdentifiers
  };
};

const mapDispatchToProps = {
  uploadFile,
  fetchRoomOptionsForAddArrivalRoomCheck,
  change
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'addArrivalRoomCheck',
    onSubmit: formSubmit,
    enableReinitialize: true
  })(AddArrivalRoomCheckForm)
) as React.ComponentType<any>;
